import {createAsyncThunk} from '@reduxjs/toolkit';

import {TAdditionalFileData} from 'components/documents/file-modal/hooks/documents-upload-actions-hook';
import {METADATA_MODULE} from 'entities/metadata';
import {selectContextRawData} from 'modules/context/context-selector';
import {resetLoadedData} from 'modules/data';
import {TLookupValue} from 'modules/data/data-types';
import {convertParamsForRequest} from 'modules/data/utils/data.utils';
import {EEntityType} from 'shared/constants/entities';
import {ECommonMessages} from 'shared/constants/messages';
import {ILookupEntry} from 'shared/types/lookups';
import {
    getPrefixedSection,
    isObject, showMessage, showMessageFromResponse,
} from 'shared/utils';
import {asyncPool} from 'shared/utils/request';
import {TAppState} from 'store/config/types';
import {tableReportSliceActions} from 'store/slices/table-report-slice';
import {normalizeBoolValue} from 'utils/common';

import {selectUserInfo} from '../../auth-slice';
import {
    generateSignatureInfoFromResponse,
    generateUploadFileRequest,
    generateUploadSignRequest,
    getUploadProgressPercent,
    normalizeFlagForUploadFile,
} from '../lib/utils';
import {DOCUMENTS_SLICE, FILE_UNIQUE_FIELD_NAME, SIGN_UNIQUE_FIELD_NAME} from '../model/constants';
import {
    selectDocumentFileUploadByEntityName,
    selectDocumentBranchNames,
    selectDocumentSignatureUploadByEntityName,
} from '../model/selectors';
import {
    setDocumentsUploadMonitoring,
    setFileActivityState,
    setSignActivityState,
} from '../model/slice';
import {
    EDocumentsSigningMethod,
    IDocumentTypeResponse,
    IDocumentUploadFile,
    IDocumentUploadSignature,
    IDocumentUploadSignatureResponse,
    EFileParsingStatus,
    EFileUploadStatus,
    IFileUploadType,
    ISignExtensionsState,
    EXsdValidationStatusCodes,
} from '../model/types';
import {
    fetchAcceptableExtensions,
    fetchBranchesLinkingFlag,
    fetchDocumentTypes,
    fetchDocumentTypesOnUpload,
    fetchDocumentsForSign,
    fetchDocumentsForUploadAttach,
    uploadDocumentFileRequest,
    IUploadFilesRequestOptions,
    fetchBranchNames,
    fetchDocumentsSigningMethod,
    IParserRequestOptions,
    fetchDefaultSignInfo,
    uploadDocumentSignatureRequest,
    IFetchDocumentsForUploadAttachArgs,
    uploadFileToCache,
    handleValidateFile,
    fetchDocTypeFlag,
} from './api';

const {
    setModalReportDownloadDocuments,
} = tableReportSliceActions;

/**
 * Асинхронный экшен для загрузки документов для прикрепления в процессе загрузки.
 * Возвращает список документов для указанного подсписка.
 *
 * @param {IFetchDocumentsForUploadAttachArgs} sublistCode - Код подсписка документов, которые нужно загрузить.
 * @returns {Promise<{sublistCode: string; documents: ILookupEntry[]}>} - Объект с кодом подсписка
 * и загруженными документами.
 * @throws {string} - Сообщение об ошибке, если загрузка не удалась.
 */
export const loadDocumentsForUploadAttach = createAsyncThunk<
  {sublistCode: string; documents: ILookupEntry[]},
  IFetchDocumentsForUploadAttachArgs>(
      `${DOCUMENTS_SLICE}/loadDocumentsForUploadAttach`,
      async ({sublistCode}: IFetchDocumentsForUploadAttachArgs, {rejectWithValue}) => {
          try {
              const {data} = await fetchDocumentsForUploadAttach({sublistCode});
              return {sublistCode, documents: data};
          } catch (e) {
              return rejectWithValue(e.response?.data || 'Ошибка загрузки документов');
          }
      },
  );

/**
 * Асинхронный экшен для загрузки флага связки филиалов.
 * Возвращает булево значение, указывающее на статус связки филиалов.
 *
 * @returns {Promise<boolean>} - Булево значение, отображающее статус связки филиалов.
 * @throws {string} - Сообщение об ошибке, если загрузка не удалась.
 */
export const loadBranchesLinkingFlag = createAsyncThunk<boolean, void>(
    `${DOCUMENTS_SLICE}/loadBranchesLinkingFlag`,
    async (_, {rejectWithValue}) => {
        try {
            const {data} = await fetchBranchesLinkingFlag();
            return normalizeBoolValue(data);
        } catch (e) {
            return rejectWithValue(e.response?.data || 'Ошибка загрузки флага связки');
        }
    },
);

/**
 * Асинхронный экшен для загрузки типов документов, доступных для загрузки.
 * Возвращает список типов документов.
 *
 * @returns {Promise<IDocumentTypeResponse[]>} - Массив типов документов для загрузки.
 * @throws {string} - Сообщение об ошибке, если загрузка не удалась.
 */
export const loadDocumentTypesOnUpload = createAsyncThunk<
    IDocumentTypeResponse[],
    void>(
        `${DOCUMENTS_SLICE}/loadDocumentTypesOnUpload`,
        async (_, {rejectWithValue}) => {
            try {
                const {data} = await fetchDocumentTypesOnUpload();
                return data;
            } catch (e) {
                return rejectWithValue(e.response?.data || 'Ошибка загрузки типов документов');
            }
        },
    );

/**
 * Асинхронный экшен для загрузки типов документов.
 * Возвращает список типов документов.
 *
 * @returns {Promise<IDocumentTypeResponse[]>} - Массив типов документов.
 * @throws {IDocumentTypeResponse[]} - В случае ошибки возвращает пустой массив и показывает сообщение об ошибке.
 */
export const loadDocumentTypes = createAsyncThunk<
  IDocumentTypeResponse[],
  void>(
      `${DOCUMENTS_SLICE}/loadDocumentTypes`,
      async (_, {rejectWithValue}) => {
          try {
              const {data} = await fetchDocumentTypes();
              return data;
          } catch (e) {
              showMessageFromResponse({response: e.response, isError: true});
              return rejectWithValue([]);
          }
      },
  );

/**
 * Асинхронный экшен для загрузки разрешенных расширений файлов.
 * Возвращает объект с разрешенными расширениями для загрузки.
 *
 * @returns {Promise<ISignExtensionsState>} - Объект, содержащий допустимые расширения для загрузки.
 * @throws {string} - В случае ошибки возвращает сообщение об ошибке загрузки разрешенных расширений.
 */
export const loadAcceptableExtensions = createAsyncThunk<ISignExtensionsState, void>(
    `${DOCUMENTS_SLICE}/loadAcceptableExtensions`,
    async (_, {rejectWithValue}) => {
        try {
            const {data} = await fetchAcceptableExtensions();
            return {accept: data} as ISignExtensionsState;
        } catch (e) {
            showMessageFromResponse({response: e.response, isError: true});
            return rejectWithValue('Ошибка загрузки разрешенных расширений');
        }
    },
);

/**
 * Асинхронный экшен для загрузки документов для подписи.
 * Возвращает массив документов для подписания в зависимости от переданных данных.
 *
 * @param {Object} params - Параметры для запроса.
 * @param {string} params.entityName - Имя сущности, для которой загружаются документы.
 *
 * @returns {Promise<IDocumentTypeResponse[] | TLookupValue[]>} - Массив документов или значений для подписания.
 * @throws {string} - В случае ошибки возвращает сообщение об ошибке загрузки документов на подпись.
 */
export const loadDocumentsForSign = createAsyncThunk<
    IDocumentTypeResponse[] | TLookupValue[],
    {entityName: string}>(
        `${DOCUMENTS_SLICE}/loadDocumentsForSign`,
        async ({entityName}, {getState, rejectWithValue}) => {
            const state = getState() as TAppState;

            try {
                const contextData = selectContextRawData(state) ?? {};
                const contextFilter = convertParamsForRequest(contextData);
                const contextParam = {
                    ...contextFilter,
                    entityName: getPrefixedSection(entityName),
                };
                const {data} = await fetchDocumentsForSign(contextParam);

                return data as IDocumentTypeResponse[];
            } catch (e) {
                showMessageFromResponse({response: e.response, isError: true});
                return rejectWithValue('Ошибка загрузки документов на подпись');
            }
        },
    );

/**
 * Асинхронный экшен для загрузки информации о филиалах.
 * Если данные уже загружены и параметр `resetBranchNames` равен `false`, данные не запрашиваются снова.
 * В случае ошибки выводится сообщение об ошибке загрузки филиалов.
 *
 * @param {boolean | undefined} resetBranchNames - Флаг, который указывает,
 * нужно ли сбросить текущие данные и загрузить новые.
 *
 * @returns {Promise<IDocumentTypeResponse[]>} - Массив объектов с данными филиалов.
 * @throws {string} - В случае ошибки возвращается сообщение об ошибке загрузки филиалов.
 */
export const loadBranchNames = createAsyncThunk<IDocumentTypeResponse[], boolean | undefined>(
    `${DOCUMENTS_SLICE}/loadBranchNames`,
    async (resetBranchNames, {getState, rejectWithValue}) => {
        const state = getState() as TAppState;

        const currentBranchNames = selectDocumentBranchNames(state);

        if (currentBranchNames && !resetBranchNames) {
            return currentBranchNames; // Возвращаем текущие данные, если они есть
        }

        try {
            const {data: branchNames} = await fetchBranchNames();
            return branchNames; // fulfilled сам обновит `state`
        } catch (e: any) {
            showMessageFromResponse({response: e.response, isError: true});
            return rejectWithValue(e.response?.data || 'Ошибка загрузки филиалов');
        }
    },
);

/**
 * Асинхронный экшен для загрузки метода подписания документов.
 * В случае ошибки возвращается сообщение об ошибке загрузки метода подписания.
 *
 * @returns {Promise<EDocumentsSigningMethod | undefined>} - Метод подписания документов.
 * @throws {string} - В случае ошибки возвращается сообщение об ошибке загрузки метода подписания.
 */
export const loadDocumentsSigningMethod = createAsyncThunk<
    EDocumentsSigningMethod | undefined,
    void
>(
    `${DOCUMENTS_SLICE}/loadDocumentsSigningMethod`,
    async (_, {rejectWithValue}) => {
        try {
            const {data: signingMethod} = await fetchDocumentsSigningMethod();
            return signingMethod;
        } catch (e: any) {
            showMessageFromResponse({response: e.response, isError: true});
            return rejectWithValue(e.response?.data || 'Ошибка загрузки метода подписания');
        }
    },
);

/**
 * Асинхронный экшен для загрузки информации по подписям по файлам.
 *
 * @param {string} entityName - Название сущности.
 * @param {File[]} files - Список файлов для подписания.
 * @returns {Promise<{entityName: string, signInfo: IDocumentUploadSignature[]}>} - Возвращает объект
 * с названием сущности и списком подписей.
 * @throws {string} - В случае ошибки возвращается сообщение об ошибке.
 */
export const loadDefaultSignInfo = createAsyncThunk<
    {entityName: string; signInfo: IDocumentUploadSignature[]},
    {entityName: string; files: File[]},
    {rejectValue: string}
>(
    'documents/loadDefaultSignInfo',
    async (
        {entityName, files},
        {getState},
    ) => {
        const state = getState() as TAppState;

        const contextData = selectContextRawData(state);
        const contextFilter = convertParamsForRequest(contextData || {});
        const oldSignList = selectDocumentSignatureUploadByEntityName(entityName)(state)?.signList || [];

        const requestData = {
            ...contextFilter,
            entityName: getPrefixedSection(entityName),
            files,
        };

        const {data} = await fetchDefaultSignInfo(requestData);

        const signInfo: IDocumentUploadSignature[] = [];

        // Проходим по полученным данным и сопоставляем с файлами
        data.forEach((signFile: IDocumentUploadSignatureResponse) => {
            const correspondingFile = files.find(file => file.name === signFile.signatureFileName);
            if (correspondingFile) {
                signInfo.push({
                    file: correspondingFile,
                    fileName: correspondingFile.name,
                    createdAt: new Date(),
                    ...generateSignatureInfoFromResponse(signFile),
                });
            }
        });

        return {
            entityName,
            signInfo: [...oldSignList, ...signInfo],
        };
    },
);

/**
 * Асинхронная операция для загрузки файлов.
 *
 * @param files - Массив файлов, которые нужно загрузить.
 * @param entityName - Название сущности, к которой привязаны файлы.
 * @param useUploadDate - Флаг, указывающий, нужно ли использовать дату загрузки.
 *
 * @returns {Promise<void>} - Не возвращает данных, выполняет побочные эффекты.
 */
export const uploadDocumentFiles = createAsyncThunk<
    void, // поскольку это побочный эффект, возвращаем void
    {
        files: IDocumentUploadFile[];
        entityName: string;
        useUploadDate?: boolean;
        shouldUnzip?: boolean;
    }
>(
    `${DOCUMENTS_SLICE}/uploadFiles`,
    async ({files, entityName, useUploadDate, shouldUnzip}, {dispatch, getState}) => {
        const uniqueFieldName = FILE_UNIQUE_FIELD_NAME;

        if (uniqueFieldName.length === 0) {
            console.error('Не выбрано уникальное поле для файла.');
            return;
        }

        const state = getState() as TAppState;

        const fileUploadSlice = selectDocumentFileUploadByEntityName(entityName)(state);
        const title = state?.[METADATA_MODULE]?.[entityName]?.[EEntityType.TABLE]?.title;
        const contextData = selectContextRawData(state) ?? {};
        const userInfo = selectUserInfo(state);
        const userId = userInfo?.userId;

        const params = (contextData && userInfo && {
            organizationId: contextData.organizationId,
            taxPeriodId: contextData.taxPeriodId,
            userId,
        });

        const filteredFiles = files.filter(file => {
            const activityList = fileUploadSlice?.activityList?.[file[uniqueFieldName] as string];
            const isEmptyActivityList = !activityList;
            const isException = activityList?.uploadStatus === EFileUploadStatus.EXCEPTION;
            const isEmptyStatus = activityList?.uploadStatus === undefined;

            return isEmptyActivityList || isException || isEmptyStatus;
        });

        const convertedFiles = generateUploadFileRequest(filteredFiles, useUploadDate, true, shouldUnzip);
        const requestResult: Record<string, any> = {};
        const getUniqueValue = (data?: Record<string, any>) => (
            isObject(data) ? (data as Record<string, any>)[uniqueFieldName] : undefined
        );

        const changeActivityState = (
            uniqueValue: string,
            item: IFileUploadType,
        ) => dispatch(setFileActivityState({entityName, uniqueValue, item}));

        const createParsingHandlers = (uniqueValue: string): IParserRequestOptions => {
            let parsingPercent = 0;
            let intervalId: number | undefined;

            return {
                onParsingStart: () => {
                    intervalId = setInterval(() => {
                        if (parsingPercent === 92) {
                            clearInterval(intervalId);
                        }

                        parsingPercent += 4;

                        changeActivityState(uniqueValue, {
                            parsingPercent,
                            parsingStatus: EFileParsingStatus.ACTIVE,
                        });
                    }, 4800) as unknown as number;
                },
                onParsingSuccess: (message?: string) => {
                    changeActivityState(uniqueValue, {
                        parsingPercent: 100,
                        parsingStatus: EFileParsingStatus.SUCCESS,
                    });

                    clearInterval(intervalId);

                    requestResult[uniqueValue] = message;
                },
                onParsingException: (message?: string) => {
                    changeActivityState(uniqueValue, {
                        parsingStatus: EFileParsingStatus.EXCEPTION,
                    });

                    clearInterval(intervalId);

                    requestResult[uniqueValue] = message;
                },
            };
        };

        const createRequestOptions = (uniqueValue: string, shouldUseFileLoadStatus: boolean): IUploadFilesRequestOptions => ({
            onFileUploadProgress: (progressEvent: ProgressEvent) => {
                const percent = getUploadProgressPercent(progressEvent);
                const uploadPercent = percent === 100 ? 99 : percent;
                const uploadStatus = EFileUploadStatus.NORMAL;
                if (shouldUseFileLoadStatus) changeActivityState(uniqueValue, {uploadPercent, uploadStatus});
            },
            onFileUploadSuccess: () => {
                if (shouldUseFileLoadStatus) changeActivityState(
                    uniqueValue, {
                    uploadPercent: 100,
                    uploadStatus: EFileUploadStatus.SUCCESS,
                });
            },
            onFileUploadException: (message?: string) => {
                if (shouldUseFileLoadStatus) changeActivityState(
                    uniqueValue, {
                    uploadStatus: EFileUploadStatus.EXCEPTION,
                });

                requestResult[uniqueValue] = message;
            },
            ...createParsingHandlers(uniqueValue),
        });

        try {
            if (filteredFiles.length) {
                showMessage({
                    message: 'Программа запущена\n',
                    modalData: {
                        text: 'Посмотреть результат\n',
                        onClick: () => dispatch(
                            setModalReportDownloadDocuments({isOpen: true, wereDocumentsRead: true}),
                        ),
                    },
                });

                const savedFilesInfo: {fileName: string; saveResult: any}[] = [];

                await asyncPool(4, convertedFiles, async (file) => {
                    const shouldUseFileLoadStatus = !!file?.file;
                    const uniqueValue = getUniqueValue(file);

                    if (!shouldUseFileLoadStatus) changeActivityState(uniqueValue, {
                        uploadPercent: 0,
                        uploadStatus: EFileUploadStatus.ACTIVE,
                    });

                    const fileSaveResult = await uploadDocumentFileRequest(
                        file,
                        createRequestOptions(getUniqueValue(file), shouldUseFileLoadStatus),
                        {...params},
                    );

                    if (fileSaveResult?.status === 200) {
                        savedFilesInfo.push({
                            fileName: String(file.fileName),
                            saveResult: fileSaveResult?.data,
                        });
                        changeActivityState(getUniqueValue(file), {
                            uploadStatus: EFileUploadStatus.SUCCESS,
                        });
                    } else {
                        changeActivityState(getUniqueValue(file), {
                            uploadStatus: EFileUploadStatus.EXCEPTION,
                        });
                    }
                });

                if (savedFilesInfo.length) {
                    dispatch(setDocumentsUploadMonitoring({headers: title, fileList: savedFilesInfo}));
                }
            } else {
                showMessage({message: ECommonMessages.UPLOAD_FILES, isError: true});
            }
        } catch (e) {
            console.error(e);
        }
    },
);

/**
 * Асинхронная функция для загрузки файлов подписей на сервер с помощью createAsyncThunk.
 *
 * Функция загружает файлы подписей, предварительно фильтруя их по статусу, и отправляет на сервер с использованием
 * вспомогательных функций для отслеживания прогресса загрузки, ошибок и успешных загрузок.
 *
 * @param {Object} params - Параметры для загрузки файлов.
 * @param {IDocumentUploadSignature[]} params.files - Массив файлов для загрузки. Каждый файл должен быть
 *        типа `DocumentUploadSignature`.
 * @param {string} params.entityName - Имя сущности, к которой принадлежат файлы.
 * @param {string} [params.uniqueFieldName='fileName'] -
 * Необязательное поле, по которому будет осуществляться фильтрация файлов.
 *
 * @param {Object} thunkAPI - API для работы с Redux store, которое предоставляет методы для работы с состоянием.
 * @param {Function} thunkAPI.dispatch - Функция для отправки действий в Redux store.
 * @param {Function} thunkAPI.getState - Функция для получения текущего состояния приложения.
 *
 * @returns {void} Возвращает ничего. В случае ошибки или отсутствия файлов для загрузки, выводится сообщение.
 *
 * @throws {Error} В случае возникновения ошибки при загрузке файлов.
 */
export const uploadSignatureFiles = createAsyncThunk(
    `${DOCUMENTS_SLICE}/uploadSignatureFiles`,
    async ({files, entityName, uniqueFieldName = SIGN_UNIQUE_FIELD_NAME}: {
      files: IDocumentUploadSignature[];
      entityName: string;
      uniqueFieldName?: string;
    },
    {dispatch, getState}) => {
        if (!uniqueFieldName || uniqueFieldName.length === 0) {
            console.error('Не выбрано уникальное поле для файла.');
            return;
        }

        const state = getState() as TAppState;

        const signUploadSlice = selectDocumentSignatureUploadByEntityName(entityName)(state);
        const filteredFiles = files.filter(file => {
            const activityList = signUploadSlice?.activityList?.[file[uniqueFieldName] as string];
            const isEmptyActivityList = !activityList;
            const isException = activityList?.uploadStatus === EFileUploadStatus.EXCEPTION;
            const isEmptyStatus = activityList?.uploadStatus === undefined;
            return isEmptyActivityList || isException || isEmptyStatus;
        });

        const getUniqueValue = (data?: Record<string, any>) => (
            isObject(data) ? (data as Record<string, any>)[uniqueFieldName] : undefined
        );

        if (filteredFiles.length === 0) {
            showMessage({message: 'Нет файлов для загрузки', isError: true});
            return;
        }

        const requestResult: Record<string, any> = {};
        const failedFiles = new Set<string>();

        const createRequestOptions = (uniqueValue: string) => ({
            onFileUploadProgress: (progressEvent: Record<string, any>) => {
                const percent = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
                const uploadStatus = percent === 100 ? EFileUploadStatus.NORMAL : EFileUploadStatus.NORMAL;

                dispatch(setSignActivityState({entityName, uniqueValue, item: {uploadPercent: percent, uploadStatus}}));
            },
            onFileUploadException: (message?: string) => {
                dispatch(setSignActivityState({
                    entityName,
                    uniqueValue,
                    item: {uploadPercent: 100, uploadStatus: EFileUploadStatus.EXCEPTION},
                }));
                requestResult[uniqueValue] = message ?? 'Загрузка не выполнена';
                failedFiles.add(uniqueValue);
            },
            onFileUploadSuccess: (message?: string) => {
                dispatch(setSignActivityState({
                    entityName,
                    uniqueValue,
                    item: {uploadPercent: 100, uploadStatus: EFileUploadStatus.SUCCESS},
                }));
                requestResult[uniqueValue] = message;
            },
        });

        // Преобразуем файлы перед отправкой на сервер
        const convertedFiles = generateUploadSignRequest(filteredFiles);

        try {
            // Показываем сообщение о начале загрузки
            showMessage({
                message:
              `Загрузка начата.\n
              По окончанию загрузки появится уведомление.\n
              Результат можно будет посмотреть в данном модальном окне.\n
              Не закрывайте приложение до окончания загрузки,
              иначе файлы могут не сохраниться.`,
            });
            // Выполняем асинхронные запросы для каждого файла
            await Promise.all(convertedFiles.map(data => uploadDocumentSignatureRequest(
                data,
                createRequestOptions(getUniqueValue(data)),
            )));

            // Показываем результат загрузки
            const totalFilesCount = Object.keys(requestResult).length;
            const failedFilesCount = failedFiles.size;
            const hasErrors = failedFilesCount > 0;
            const isAllFailed = failedFilesCount === totalFilesCount;

            let header = 'Загрузка завершена.';
            if (isAllFailed) {
                header = 'Загрузка не выполнена.';
            } else if (hasErrors) {
                header = 'Загрузка завершена с ошибками.';
            }

            const resultMessages = Object
                .keys(requestResult)
                .reduce((acc: string, resultKey: string) => (`${acc + resultKey}: ${requestResult[resultKey]}\n`),
                    `${header}\nРезультат загрузки файлов:\n`);

            showMessage({message: resultMessages, isError: hasErrors});

            // Сбрасываем данные для загруженных файлов
            files.forEach(fileInfo => {
                if (fileInfo.subsectionCode) {
                    // TODO подумать как обойти каст к any
                    dispatch(resetLoadedData(fileInfo.subsectionCode) as any);
                }
            });
        } catch (error) {
            console.error('Произошла ошибка при загрузке файлов ЭП:', error);
            showMessage({message: 'Произошла ошибка при загрузке файлов', isError: true});
        }
    },
);

/**
 * Загрузка данных состояния строки файла в редактируемой таблице.
 * Этот `createAsyncThunk` выполняет асинхронную загрузку данных
 * для конкретной строки таблицы, проверку статуса файла и обновление состояния активности.
 *
 * @param {object} args - Аргументы для загрузки данных.
 * @param {IDocumentUploadFile} args.tableRow - Объект, содержащий данные строки таблицы,
 * такие как тип документа и файл.
 * @param {string} [args.entityName='TOTAL_ENTITY'] - Название сущности,
 * к которой относится данный файл. По умолчанию используется 'TOTAL_ENTITY'.
 * @param {string} [args.uniqueFieldName='fileName'] - Имя уникального поля,
 * которое будет использоваться для идентификации файла.
 *
 * @returns {object} Возвращает объект с обновленным состоянием файла и его активности.
 * @returns {string} entityName - Название сущности.
 * @returns {string} uniqueFieldName - Уникальное значение для идентификации строки таблицы.
 * @returns {updatedFile} updatedFile - Обновленный файл с измененной информацией.
 *
 * @throws {string} Если возникла ошибка при загрузке данных, возвращает строку с описанием ошибки.
 */
export const loadFileRowStateDataInEditableTable = createAsyncThunk<
{
    entityName: string;
    uniqueFieldName: string;
    updatedFile: IDocumentUploadFile;
  },
  {
    tableRow: IDocumentUploadFile;
    entityName?: string;
    uniqueFieldName?: string;
    additionalFileData?: TAdditionalFileData;
  },
  {state: TAppState; rejectValue: string}
>(
    `${DOCUMENTS_SLICE}/loadFileRowStateDataInEditableTable`,
    async (
        {
            tableRow, entityName = 'TOTAL_ENTITY', uniqueFieldName = FILE_UNIQUE_FIELD_NAME, additionalFileData = [],
        },
        {dispatch, getState, rejectWithValue},
    ) => {
        const state = getState() as TAppState;

        const uniqueValue = tableRow[uniqueFieldName];
        const fileUploadSlice = selectDocumentFileUploadByEntityName(entityName)(state);
        const {fileList, sessionId} = fileUploadSlice;
        const fileIndex = fileList.findIndex((file: IDocumentUploadFile) => file[uniqueFieldName] === uniqueValue);
        let fileUploadInterfaceId = fileList[fileIndex]?.fileUploadInterfaceId || null;
        const isFileCached = Boolean(fileUploadInterfaceId);

        const activityListItem = fileUploadSlice?.activityList?.[uniqueValue as string];
        const existingPermissions = activityListItem?.editFieldsPermission || {};

        if (!isFileCached) {
            try {
                const uploadedFileRes = await uploadFileToCache(
                    {tableRow, sessionId: sessionId!},
                );

                fileUploadInterfaceId = String(uploadedFileRes?.id);
            } catch (error) {
                if (error?.response?.status === 422) {
                    showMessageFromResponse({response: error.response, isError: true});
                }
                console.error('Возникла ошибка при загрузке файла в кэш', error);
            }
        }

        const {validationData, updatedFileUploadInterfaceId} = await handleValidateFile({
            tableRow,
            fileUploadInterfaceId,
            sessionId: sessionId!,
        });

        fileUploadInterfaceId = updatedFileUploadInterfaceId;

        try {
            let uploadDate;
            let correctionNumber;
            let regNum;
            if (tableRow.type) {
                additionalFileData[tableRow.type as number] ??= Promise.all([
                    fetchDocTypeFlag('get-timestamp', tableRow.type as string),
                    fetchDocTypeFlag('get-corrnum', tableRow.type as string),
                    fetchDocTypeFlag('get-regnum', tableRow.type as string),
                ]).then(responses => {
                    const [uploadDateFlag, corrNumFlag, regNumFlag] = responses.map(r => r.data);
                    return {
                        uploadDate: uploadDateFlag,
                        correctionNumber: corrNumFlag,
                        regNum: regNumFlag,
                    };
                });

                const flagData = await additionalFileData[tableRow.type as number];
                uploadDate = flagData.uploadDate;
                correctionNumber = flagData.correctionNumber;
                regNum = flagData.regNum;
            }

            const uploadDateActiveState = normalizeFlagForUploadFile(uploadDate);
            const correctionNumberActiveState = normalizeFlagForUploadFile(correctionNumber);
            const regNumActiveState = normalizeFlagForUploadFile(regNum);

            const updatedFile: IDocumentUploadFile = {
                ...tableRow,
                xsdValidationStatusCode: validationData?.validationResult?.statusCode
                ?? EXsdValidationStatusCodes.NOT_APPLICABLE,
                xsdValidationStatus: validationData?.validationResult?.meaning ?? '—',
                xsdValidationStatusLog: validationData?.validationResult?.description,
                fileUploadInterfaceId,
                correctionNumber: correctionNumberActiveState.activated ? tableRow?.correctionNumber : undefined,
                uploadDate: uploadDateActiveState.activated ? tableRow?.uploadDate : null,
                regNum: regNumActiveState.activated ? tableRow?.regNum : undefined,
            } as IDocumentUploadFile;

            const activityState: IFileUploadType = {
                editFieldsPermission: {
                    ...existingPermissions,
                    uploadDate: uploadDateActiveState,
                    correctionNumber: correctionNumberActiveState,
                    regNum: regNumActiveState,
                },
            };

            dispatch(setFileActivityState({entityName, uniqueValue: uniqueValue as string, item: activityState}));

            return {
                entityName,
                uniqueFieldName,
                updatedFile,
            };
        } catch (e: any) {
            return rejectWithValue(e.response?.data || 'Ошибка при загрузке данных');
        }
    },
);
